import dayjs from 'dayjs';
import { normalizeFileName } from '../../../components/ui/utils/fileNameEncoding';

/**
 * Export risk assessment report to PDF
 * Clean, professional design matching form structure
 */
export const exportRiskAssessmentToPdf = async (riskAssessment, includeAttachments = false) => {
  try {
    // Dynamic imports to avoid circular dependencies
    const pdfMake = await import('pdfmake/build/pdfmake');
    const pdfFonts = await import('pdfmake/build/vfs_fonts');
    const { riskAssessmentConfig } = await import('../riskAssessmentConfig');
    const { API_BASE_URL, API_ENDPOINTS } = await import('../../../config/apiConfig');
    
    pdfMake.default.vfs = pdfFonts.vfs || pdfFonts.default || {};

    // Define logo image key for pdfMake
    const logoImageKey = 'companyLogo';

    // Fetch company logo from application settings
    let companyLogoBase64 = null;
    try {
      const token = localStorage.getItem('authToken');
      const logoSettingResponse = await fetch(`${API_BASE_URL}/application-setting/key/companyLogo`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (logoSettingResponse.ok) {
        const logoSetting = await logoSettingResponse.json();
        if (logoSetting && logoSetting.value && logoSetting.isActive !== false) {
          let logoUrl = logoSetting.value.trim();
          
          // Construct full URL if not already absolute
          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
            logoUrl = logoUrl.startsWith('/') 
              ? `${baseUrl}${logoUrl}` 
              : `${baseUrl}/${logoUrl}`;
          }

          // Fetch logo and convert to base64
          const logoResponse = await fetch(logoUrl, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });

          if (logoResponse.ok) {
            // Check if it's SVG first (by URL extension or content type)
            const isSvg = logoUrl.toLowerCase().endsWith('.svg');
            
            if (isSvg) {
              // Convert SVG to PNG for pdfMake compatibility
              const svgText = await logoResponse.text();
              companyLogoBase64 = await new Promise((resolve, reject) => {
                const img = new Image();
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Create a data URL from the SVG
                const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(svgBlob);
                
                img.onload = () => {
                  try {
                    const maxWidth = 300;
                    const scale = img.width > maxWidth ? maxWidth / img.width : 1;
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    
                    const pngDataUrl = canvas.toDataURL('image/png');
                    const base64data = pngDataUrl.split(',')[1];
                    
                    URL.revokeObjectURL(url);
                    resolve({
                      data: base64data,
                      mimeType: 'image/png'
                    });
                    console.log('✅ Company logo (SVG) converted to PNG for PDF export');
                  } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                  }
                };
                
                img.onerror = () => {
                  URL.revokeObjectURL(url);
                  reject(new Error('Failed to load SVG image'));
                };
                
                img.src = url;
              });
            } else {
              // For non-SVG images (PNG, JPG, etc.), use directly
              const clonedResponse = logoResponse.clone();
              const logoBlob = await clonedResponse.blob();
              companyLogoBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64data = reader.result.split(',')[1];
                  resolve({
                    data: base64data,
                    mimeType: logoBlob.type || 'image/png'
                  });
                };
                reader.onerror = reject;
                reader.readAsDataURL(logoBlob);
              });
              console.log('✅ Company logo loaded for PDF export');
            }
          } else {
            console.warn('⚠️ Failed to fetch logo file:', logoResponse.status);
          }
        }
      } else {
        console.warn('⚠️ Failed to fetch logo setting:', logoSettingResponse.status);
      }
    } catch (error) {
      console.warn('⚠️ Error fetching company logo for PDF:', error);
      // Logo is optional, continue without it
    }

    // Helper function to fetch file as base64
    const fetchFileAsBase64 = async (fileId, fileName) => {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        const response = await fetch(API_ENDPOINTS.fileAttachment.download(fileId), {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });

        if (!response.ok) {
          console.warn(`Failed to fetch file ${fileName}:`, response.statusText);
          return null;
        }

        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result.split(',')[1]; // Remove data:type;base64, prefix
            resolve({
              data: base64data,
              mimeType: blob.type || 'application/octet-stream',
              fileName: fileName
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn(`Error fetching file ${fileName}:`, error);
        return null;
      }
    };

    // Fetch all file attachments as base64 if includeAttachments is true
    let fileAttachmentsData = [];
    if (includeAttachments && riskAssessment.fileAttachments && riskAssessment.fileAttachments.length > 0) {
      const activeFiles = riskAssessment.fileAttachments.filter(file => !file.isDeleted);
      console.log(`Fetching ${activeFiles.length} file attachments...`);
      
      for (const file of activeFiles) {
        const fileData = await fetchFileAsBase64(file.id, normalizeFileName(file.fileName || ''));
        if (fileData) {
          fileAttachmentsData.push({
            ...fileData,
            originalFile: file
          });
        }
      }
      console.log(`Successfully fetched ${fileAttachmentsData.length} file attachments`);
    }

    // Fetch approval requests if not included in risk assessment data
    let approvalRequests = riskAssessment.approvalRequests;
    if (!approvalRequests && riskAssessment.id) {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        // Entity type for risk assessments is "risk_assessment", entityTypeId is 4
        const response = await fetch(`${API_BASE_URL}/approval-requests/entity/risk_assessment/${riskAssessment.id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });
        
        if (response.ok) {
          approvalRequests = await response.json();
          console.log('Fetched approval requests:', approvalRequests);
        }
      } catch (error) {
        console.warn('Failed to fetch approval requests:', error);
      }
    }

    // Merge approval requests into risk assessment object
    const riskAssessmentWithApprovals = {
      ...riskAssessment,
      approvalRequests: approvalRequests || []
    };

    // Helper functions
    const formatDate = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch {
        return date;
      }
    };

    const formatDateOnly = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch {
        return date;
      }
    };

    const formatArray = (arr, field = 'name') => {
      if (!arr || !Array.isArray(arr) || arr.length === 0) return 'N/A';
      return arr.map(item => {
        if (typeof item === 'object') {
          return item.userDisplayName || item.name || item.title || item.label || 
                 (item.userFirstName && item.userLastName ? `${item.userFirstName} ${item.userLastName}` : 'N/A');
        }
        return item;
      }).join(', ');
    };

    // Use risk assessment with approvals
    const riskAssessmentData = riskAssessmentWithApprovals;

    const { fetchRiskAssessmentReferenceDocument, formatRiskAssessmentReferenceDocumentText } =
      await import('./fetchRiskAssessmentReferenceDocument');
    let referenceDocumentText = riskAssessmentData.referenceDocument || null;
    if (!referenceDocumentText) {
      const referenceDocument = await fetchRiskAssessmentReferenceDocument();
      referenceDocumentText = formatRiskAssessmentReferenceDocumentText(referenceDocument);
    }

    const referenceDocumentLine = {
      text: referenceDocumentText || 'N/A',
      style: 'reportSubtitle',
      alignment: 'right',
      margin: [0, 0, 0, 5],
    };

    // Get form sections from config
    const formSections = riskAssessmentConfig.formSections || [];

    // Section renderers based on form sections
    const renderSection = (section) => {
      const title = section.title || '';
      
      // Field value getter
      const getFieldValue = (fieldName) => {
        const field = riskAssessmentConfig.fields.find(f => f.name === fieldName);
        if (!field) return null;

        const value = riskAssessmentData[fieldName];
        if (value === null || value === undefined) return null;

        // Handle relations
        if (field.lookupEndpoint) {
          const relationName = field.table?.displayField?.split('.')[0] || field.name.replace('Id', '');
          const relation = riskAssessmentData[relationName];
          if (relation) {
            if (Array.isArray(relation)) {
              return relation.map(r => r.name || r.userDisplayName || r).join(', ');
            }
            return relation.name || relation.userDisplayName || relation.code || relation.title || value;
          }
        }

        // Handle arrays
        if (Array.isArray(value)) {
          return formatArray(value);
        }

        // Handle dates
        if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
          return formatDateOnly(value);
        }

        return value;
      };

      // Skip custom component sections
      if (section.layout === 'custom' && section.customComponent) {
        return null;
      }

      // Skip file attachments section (handled separately)
      if (section.fields && section.fields.includes('fileAttachments')) {
        return null;
      }

      // Skip sections without fields
      if (!section.fields || !Array.isArray(section.fields) || section.fields.length === 0) {
        return null;
      }

      const sectionFields = section.fields
        .map(fieldName => {
          const value = getFieldValue(fieldName);
          if (value === null || value === undefined || value === '') return null;

          const field = riskAssessmentConfig.fields.find(f => f.name === fieldName);
          const label = field?.label || fieldName;

          return {
            columns: [
              { text: `${label}:`, style: 'fieldLabel', width: '35%' },
              { text: String(value), style: 'fieldValue', width: '65%' }
            ],
            margin: [0, 0, 0, 5]
          };
        })
        .filter(Boolean);

      if (sectionFields.length === 0) return null;

      return {
        stack: [
          {
            text: title.toUpperCase(),
            style: 'sectionTitle',
            margin: [0, 0, 0, 12]
          },
          ...sectionFields
        ],
        margin: [0, 0, 0, 20],
        pageBreak: 'avoid'
      };
    };

    // Build content array
    const headerContent = [];
    
    // Add big logo on first page if available
    if (companyLogoBase64) {
      headerContent.push({
        columns: [
          {
            image: logoImageKey,
            width: 200,
            alignment: 'left',
            margin: [0, 0, 0, 20]
          },
          {
            stack: [
              {
                text: 'RISK ASSESSMENT REPORT',
                style: 'reportTitle',
                alignment: 'right',
                margin: [0, 0, 0, 5]
              },
              referenceDocumentLine
            ],
            alignment: 'right',
            width: '*'
          }
        ],
        margin: [0, 0, 0, 20]
      });
    } else {
      // No logo, center the title
      headerContent.push(
        {
          text: 'RISK ASSESSMENT REPORT',
          style: 'reportTitle',
          alignment: 'center',
          margin: [0, 0, 0, 10],
        },
        {
          ...referenceDocumentLine,
          alignment: 'center',
        },
      );
    }
    
    headerContent.push({
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: '#cccccc'
        }
      ],
      margin: [0, 0, 0, 20]
    });

    const content = [
      // Header
      {
        stack: headerContent
      },
      // Render all sections in config order
      ...formSections.map(section => renderSection(section)).filter(Boolean),
      // Actions Section
      ...(riskAssessmentData.riskAssessmentActions && riskAssessmentData.riskAssessmentActions.length > 0 ? [{
        stack: [
          {
            text: 'ACTIONS',
            style: 'sectionTitle',
            margin: [0, 0, 0, 12]
          },
          {
            table: {
              headerRows: 1,
              widths: ['*', 'auto', 'auto', 'auto'],
              body: [
                [
                  { text: 'Description', style: 'tableHeader' },
                  { text: 'Started', style: 'tableHeader' },
                  { text: 'Due Date', style: 'tableHeader' },
                  { text: 'Completed', style: 'tableHeader' }
                ],
                ...riskAssessmentData.riskAssessmentActions.map(action => [
                  { text: action.description || 'N/A', style: 'tableCell' },
                  { text: formatDate(action.startedAt), style: 'tableCell' },
                  { text: formatDateOnly(action.dueDate), style: 'tableCell' },
                  { text: formatDateOnly(action.closedAt), style: 'tableCell' }
                ])
              ]
            },
            layout: 'lightHorizontalLines',
            margin: [0, 5, 0, 10]
          }
        ],
        margin: [0, 0, 0, 20],
        pageBreak: 'avoid'
      }] : []),
      // File Attachments Section (same format as complaints)
      ...(riskAssessmentData.fileAttachments ? [{
        stack: [
          {
            text: 'FILE ATTACHMENTS',
            style: 'sectionTitle',
            margin: [0, 0, 0, 12]
          },
          ...(riskAssessmentData.fileAttachments && riskAssessmentData.fileAttachments.length > 0 ? [
            {
              table: {
                headerRows: 1,
                widths: ['*', 'auto', 'auto'],
                body: [
                  [
                    { text: 'File Name', style: 'tableHeader' },
                    { text: 'Size', style: 'tableHeader' },
                    { text: 'Uploaded', style: 'tableHeader' }
                  ],
                  ...riskAssessmentData.fileAttachments.filter(file => !file.isDeleted).map(file => [
                    { text: normalizeFileName(file.fileName || file.name || 'N/A'), style: 'tableCell' },
                    { text: file.fileSize ? `${(file.fileSize / 1024).toFixed(2)} KB` : 'N/A', style: 'tableCell' },
                    { text: formatDate(file.uploadedAt || file.createdAt), style: 'tableCell' }
                  ])
                ]
              },
              layout: 'lightHorizontalLines',
              margin: [0, 5, 0, 0]
            }
          ] : [
            {
              columns: [
                { text: 'File Attachments', style: 'fieldLabel', width: '35%' },
                { text: 'No attachments', style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            }
          ])
        ],
        margin: [0, 0, 0, 20],
        pageBreak: 'avoid',
        unbreakable: true
      }] : []),
      // Review and Approval Status
      ...(riskAssessmentData.approvalRequests ? [
        {
          stack: [
            {
              text: 'REVIEW AND APPROVAL STATUS',
              style: 'sectionTitle',
              margin: [0, 0, 0, 12]
            },
            ...(riskAssessmentData.approvalRequests && riskAssessmentData.approvalRequests.length > 0 
              ? riskAssessmentData.approvalRequests.map((approval, idx) => [
              {
                text: `Approval Request ${idx + 1}: ${approval.code || approval.id || 'N/A'}`,
                style: 'subsectionTitle',
                margin: [0, idx > 0 ? 15 : 0, 0, 8]
              },
              {
                columns: [
                  { text: 'Status:', style: 'fieldLabel', width: '35%' },
                  { text: approval.status?.name || 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Requested By:', style: 'fieldLabel', width: '35%' },
                  { text: approval.requestedBy?.userDisplayName || approval.requestedBy?.name || 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Requested Date:', style: 'fieldLabel', width: '35%' },
                  { text: formatDate(approval.requestedAt), style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Due Date:', style: 'fieldLabel', width: '35%' },
                  { text: formatDateOnly(approval.dueDate), style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Description:', style: 'fieldLabel', width: '35%' },
                  { text: approval.description || 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 10]
              },
              ...(approval.steps && approval.steps.length > 0 ? [
                {
                  text: 'Approval Steps:',
                  style: 'subsectionTitle',
                  margin: [0, 0, 0, 8]
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ['auto', '*', '*', '*', 'auto', '*'],
                    body: [
                      [
                        { text: 'Step', style: 'tableHeader', alignment: 'center' },
                        { text: 'Step Name', style: 'tableHeader' },
                        { text: 'Type', style: 'tableHeader' },
                        { text: 'Status', style: 'tableHeader' },
                        { text: 'Approved Date', style: 'tableHeader' },
                        { text: 'Approver/Reviewer', style: 'tableHeader' }
                      ],
                      ...approval.steps.map(step => [
                        { text: step.stepNumber?.toString() || 'N/A', style: 'tableCell', alignment: 'center' },
                        { text: step.stepName || 'N/A', style: 'tableCell' },
                        { text: step.stepType || 'N/A', style: 'tableCell' },
                        { text: step.status?.name || 'Pending', style: 'tableCell' },
                        { text: step.approvedAt ? formatDate(step.approvedAt) : (step.completedAt ? formatDate(step.completedAt) : 'N/A'), style: 'tableCell' },
                        {
                          text: [
                            step.approvedBy?.userDisplayName || step.approvedBy?.name,
                            step.reviewedBy?.userDisplayName || step.reviewedBy?.name,
                            step.assignedApprover?.userDisplayName || step.assignedApprover?.name
                          ].filter(Boolean).join(' / ') || 'N/A',
                          style: 'tableCell'
                        }
                      ])
                    ]
                  },
                  layout: 'lightHorizontalLines',
                  margin: [0, 5, 0, 10]
                }
              ] : [])
            ]).flat()
              : [
                {
                  columns: [
                    { text: 'Status:', style: 'fieldLabel', width: '35%' },
                    { text: 'No approval requests found', style: 'fieldValue', width: '65%' }
                  ],
                  margin: [0, 0, 0, 8]
                }
              ])
          ],
          margin: [0, 0, 0, 20],
          pageBreak: 'avoid',
          unbreakable: true
        }
      ] : [])
    ];

    // PDF Document Definition
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 50],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 10,
        lineHeight: 1.4,
        color: '#333333'
      },
      header: function(currentPage, pageCount, pageSize) {
        // Add logo to top left of every page except the first page (first page has big logo in content)
        if (companyLogoBase64 && currentPage > 1) {
          return {
            image: logoImageKey,
            width: 120,
            alignment: 'left',
            margin: [40, 20, 0, 0]
          };
        }
        return null;
      },
      footer: function(currentPage, pageCount) {
        // Only show footer if we're not on an empty last page
        if (currentPage > pageCount) {
          return null; // Don't render footer on empty pages
        }
        return {
          text: `Page ${currentPage} of ${pageCount} | Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
          alignment: 'center',
          fontSize: 8,
          color: '#999999',
          margin: [0, 10, 0, 0]
        };
      },
      content: content,
      // Register images for pdfMake
      images: companyLogoBase64 ? {
        [logoImageKey]: `data:${companyLogoBase64.mimeType};base64,${companyLogoBase64.data}`
      } : {},
      styles: {
        reportTitle: {
          fontSize: 18,
          bold: true,
          color: '#1a1a1a'
        },
        reportSubtitle: {
          fontSize: 12,
          color: '#666666'
        },
        sectionTitle: {
          fontSize: 14,
          bold: true,
          color: '#2c3e50',
          decoration: 'underline'
        },
        subsectionTitle: {
          fontSize: 12,
          bold: true,
          color: '#34495e'
        },
        fieldLabel: {
          fontSize: 10,
          bold: true,
          color: '#555555'
        },
        fieldValue: {
          fontSize: 10,
          color: '#333333'
        },
        tableHeader: {
          fontSize: 9,
          bold: true,
          color: '#ffffff',
          fillColor: '#34495e'
        },
        tableCell: {
          fontSize: 9,
          color: '#333333'
        }
      }
    };

    // Generate PDF
    const pdfDoc = pdfMake.default.createPdf(docDefinition);
    
    if (includeAttachments && fileAttachmentsData.length > 0) {
      // Create a ZIP file containing the PDF and all attachments
      try {
        // Import JSZip
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        
        // Generate PDF as blob and add to ZIP
        pdfDoc.getBlob((pdfBlob) => {
          // Add PDF to ZIP
          zip.file(`RiskAssessment-${riskAssessmentData.code || riskAssessmentData.id}.pdf`, pdfBlob);
          
          // Add all attachment files to ZIP
          fileAttachmentsData.forEach((fileData) => {
            const { data, fileName } = fileData;
            // Convert base64 to binary
            const binaryString = atob(data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            zip.file(fileName, bytes);
          });
          
          // Generate and download ZIP file
          zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `RiskAssessment-${riskAssessmentData.code || riskAssessmentData.id}-with-attachments.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }).catch((error) => {
            console.error('Error generating ZIP file:', error);
            // Fallback to just downloading the PDF
            pdfDoc.download(`RiskAssessment-${riskAssessmentData.code || riskAssessmentData.id}.pdf`);
          });
        });
      } catch (error) {
        console.error('Error importing JSZip or creating ZIP:', error);
        // Fallback to just downloading the PDF
        pdfDoc.download(`RiskAssessment-${riskAssessmentData.code || riskAssessmentData.id}.pdf`);
      }
    } else {
      // Just download the PDF
      pdfDoc.download(`RiskAssessment-${riskAssessmentData.code || riskAssessmentData.id}.pdf`);
    }
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};

